import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import logoImg from '../../../assets/Logo/main_logo.png';
import homeIconImg from '../../../assets/Icon/project/home_icon.png';
import pictureIconImg from '../../../assets/Icon/project/picture_icon.png';
import checkIconImg from '../../../assets/Icon/project/check_icon.png';
import kakaoIconImg from '../../../assets/Icon/project/kakao_icon.png';
import successCheckLottie from '../../../assets/Icon/project/Lottie/success_check.lottie';
import { convertToVwWeb } from '../../../Utils/responsive';
import { extractDigits, formatCurrencyInput, formatKoreanCurrency, parseCurrencyNumber } from '../../../Utils/formatCurrency';
import RichTextEditor, { type RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import { PATHS } from '../../../routes/paths';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { checkProjectId, createProject, getProject, uploadProjectImage } from '../../../Module/Project/Presentation/projectActions';
import { selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import DSModal from '../../../components/designSystem/DSModal';
import { theme } from '../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import { getYouTubeThumbnail } from '../../../Utils/youtube';
import { isImageSizeAllowed } from '../../../Utils/imageUpload';
import { openMailClient } from '../../../Utils/mail';
import { buildProjectShareMailTemplate } from '../../../Utils/shareMailTemplates';
import { getProjectShareImageUrl, shareKakaoFeed } from '../../../Utils/kakaoShare';
import { formatUserFullName } from '../../../Utils/formatUserName';
import { isIndividualUserType } from '../../../Utils/userType';
import PreviewGeneral from './components/PreviewGeneral';
import PreviewPartner from './components/PreviewPartner';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');

const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};
type FundingType = 'regular' | 'once';
type Currency = 'KRW' | 'USD';
type MediaType = 'image' | 'video';
type Membership = 'partner' | 'builder' | 'prime' | 'general';
type ProjectIdCheckState = 'idle' | 'checking' | 'available' | 'taken' | 'error';
const PROJECT_ID_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{4,20}$/;
const sanitizeProjectIdInput = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '');
const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const KAKAO_SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';

const NewProjectWeb = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation');
  const creatingLabel = stripEllipsis(t('page.newProject.actions.creating'));
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const selectedProject = useAppSelector(selectSelectedProject);
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // 페이지 배경색 유지 (스크롤 시 흰 화면 안 보이게)
  useEffect(() => {
    const pageBg = theme.colors.grayScale[6];
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    document.documentElement.style.background = pageBg;
    document.body.style.background = pageBg;
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, []);

  // step1
  const [fundingType, setFundingType] = useState<FundingType>('regular');
  const [currency, setCurrency] = useState<Currency>('KRW');
  const [goalAmount, setGoalAmount] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [projectIdCheckState, setProjectIdCheckState] = useState<ProjectIdCheckState>('idle');
  const [checkedProjectId, setCheckedProjectId] = useState<string>('');

  // Focus states for floating labels
  const [titleFocused, setTitleFocused] = useState(false);
  const [idFocused, setIdFocused] = useState(false);

  // step2
  const [mediaType, setMediaType] = useState<MediaType>('image');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoUrlFocused, setVideoUrlFocused] = useState(false);

  // step3
  const [storyHtml, setStoryHtml] = useState<string>('');


  // step4
  const [membership, setMembership] = useState<Membership>('prime');

  // step5
  const [previewOpen, setPreviewOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [createdProjectId, setCreatedProjectId] = useState<string>('');
  const [isStep5StoryExpanded, setIsStep5StoryExpanded] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  useEffect(() => {
    if (step !== 5) return;
    const hasMedia = Boolean(storyHtml && /data-embed=['"](story-image|pinned-image)['"]|<img/i.test(storyHtml));
    setIsStep5StoryExpanded(hasMedia);
  }, [step, storyHtml]);
  const kakaoSdkLoadingRef = useRef<Promise<any> | null>(null);

  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const richTextEditorRef = useRef<RichTextEditorHandle | null>(null);
  const step3ScrollRef = useRef<HTMLDivElement | null>(null);
  const projectIdCheckTimerRef = useRef<number | null>(null);

  const scrollStep3StoryEditorIntoView = useCallback(() => {
    const scrollEl = step3ScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0).getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  // 스텝 변경 시 최상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // 스텝 3 진입 시 에디터 포커스(끝) + 스크롤 영역 정렬
  useEffect(() => {
    if (step !== 3) return;
    const t = setTimeout(() => {
      richTextEditorRef.current?.focusAtEnd();
      requestAnimationFrame(() => {
        scrollStep3StoryEditorIntoView();
      });
    }, 100);
    return () => clearTimeout(t);
  }, [step, scrollStep3StoryEditorIntoView]);
  const projectIdCheckRequestSeqRef = useRef(0);
  // NOTE: Step3 이미지 삽입은 RichTextEditor 내부 input 사용

  const titleTrimmed = sanitizeProjectTitle(title).trim().slice(0, 40);
  const projectIdTrimmed = projectId.trim();
  const isProjectIdFormatValid = PROJECT_ID_REGEX.test(projectIdTrimmed);
  const isProjectIdAvailable = projectIdTrimmed.length > 0 && checkedProjectId === projectIdTrimmed && projectIdCheckState === 'available';
  const isProjectIdTaken = projectIdTrimmed.length > 0 && checkedProjectId === projectIdTrimmed && projectIdCheckState === 'taken';
  const userId =
    (session as { user?: { id?: string } } | undefined)?.user?.id ?? window.localStorage.getItem('missionfund.userId') ?? '';
  const profileFullName = formatUserFullName(userProfile?.first_name, userProfile?.last_name);
  const snsName =
    typeof userProfile?.sns_name === 'string' ? userProfile.sns_name.trim() : '';
  const sessionFallbackFullName = formatUserFullName(
    (session as { user?: { first_name?: string } } | undefined)?.user?.first_name,
    (session as { user?: { last_name?: string } } | undefined)?.user?.last_name,
  );
  const userNameFallback =
    (profileFullName ? profileFullName : undefined) ??
    (snsName ? snsName : undefined) ??
    (session as { user?: { name?: string } } | undefined)?.user?.name ??
    (sessionFallbackFullName ? sessionFallbackFullName : undefined) ??
    (session as { user?: { email?: string } } | undefined)?.user?.email ??
    '';
  const organizationName = typeof userProfile?.organization === 'string' ? userProfile.organization.trim() : '';
  const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
  const isIndividual = isIndividualUserType(userProfile?.user_type);
  const userFullName = restrictedRegion
    ? t('page.newProject.preview.securityCountryName')
    : isIndividual
      ? userNameFallback
      : (organizationName || userNameFallback);
  const projectUrl = useMemo(() => {
    const id = (createdProjectId || projectIdTrimmed || 'yourID').trim();
    return `https://go.MissionFund.org/${id}`;
  }, [createdProjectId, projectIdTrimmed]);
  const normalizedMembership = useMemo<Membership>(() => {
    const value = String(membership || 'prime').toLowerCase();
    if (value === 'general') return 'general';
    if (value === 'builder') return 'builder';
    if (value === 'prime') return 'prime';
    return 'partner';
  }, [membership]);
  const isGeneralMembership = normalizedMembership === 'general';
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const showGoalPreview = currency === 'KRW' && numericGoal >= 10000;
  const goalPreviewText = showGoalPreview ? formatKoreanCurrency(numericGoal) : '';

  const hasRepresentativeImage = useMemo(() => {
    return mediaType === 'image' && Boolean(mediaPreviewUrl);
  }, [mediaPreviewUrl, mediaType]);

  const storyEmbeddedCount = useMemo(() => {
    if (!storyHtml) return 0;
    const story = storyHtml.match(/data-embed="story-image"/g) ?? [];
    return story.length;
  }, [storyHtml]);
  const hasStoryContent = useMemo(() => {
    if (!storyHtml) return false;
    const plain = storyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return plain.length > 0;
  }, [storyHtml]);

  const videoUrlTrimmed = videoUrl.trim();
  const videoUrlValid = useMemo(() => {
    if (!videoUrlTrimmed) return false;
    try {
      const url = new URL(videoUrlTrimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }, [videoUrlTrimmed]);
  const showVideoUrlError = Boolean(videoUrlTrimmed) && !videoUrlValid;
  const videoEmbedUrl = useMemo(() => {
    if (!videoUrlValid) return '';
    try {
      const url = new URL(videoUrlTrimmed);
      const host = url.hostname.replace('www.', '');
      
      // youtu.be/ID
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      
      // youtube.com
      if (host.includes('youtube.com')) {
        // /embed/ID
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        // /shorts/ID
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        // /watch?v=ID
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  }, [videoUrlTrimmed, videoUrlValid]);
  const videoPreviewSrc = useMemo(() => {
    if (!videoUrlValid) return '';
    return videoEmbedUrl || videoUrlTrimmed;
  }, [videoEmbedUrl, videoUrlTrimmed, videoUrlValid]);

  const totalStoryPhotoCount = useMemo(() => {
    return storyEmbeddedCount;
  }, [storyEmbeddedCount]);
  const hasDraftChanges = useMemo(() => {
    if (completeOpen) return false;
    return (
      step > 1
      || fundingType !== 'regular'
      || currency !== 'KRW'
      || Boolean(goalAmount.trim())
      || Boolean(titleTrimmed)
      || Boolean(projectIdTrimmed)
      || mediaType !== 'image'
      || Boolean(mediaFile)
      || Boolean(mediaPreviewUrl)
      || Boolean(videoUrl.trim())
      || hasStoryContent
      || membership !== 'partner'
      || previewOpen
    );
  }, [
    completeOpen,
    step,
    fundingType,
    currency,
    goalAmount,
    titleTrimmed,
    projectIdTrimmed,
    mediaType,
    mediaFile,
    mediaPreviewUrl,
    videoUrl,
    hasStoryContent,
    membership,
    previewOpen,
  ]);

  useEffect(() => {
    if (!hasDraftChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasDraftChanges]);

  const projectIdStatusMessage = useMemo(() => {
    if (!projectIdTrimmed || !isProjectIdFormatValid) return '';
    if (projectIdCheckState === 'checking') return t('page.newProject.validation.projectId.checking');
    if (isProjectIdTaken) return t('page.newProject.validation.projectId.taken');
    if (isProjectIdAvailable) return t('page.newProject.validation.projectId.available');
    if (projectIdCheckState === 'error') return t('page.newProject.validation.projectId.error');
    return '';
  }, [isProjectIdAvailable, isProjectIdFormatValid, isProjectIdTaken, projectIdCheckState, projectIdTrimmed, t]);

  const projectIdStatusColor = useMemo(() => {
    if (projectIdCheckState === 'checking') return '#357B89';
    if (isProjectIdAvailable) return '#0A8A3A';
    if (isProjectIdTaken || projectIdCheckState === 'error') return '#FF0000';
    return '#357B89';
  }, [isProjectIdAvailable, isProjectIdTaken, projectIdCheckState]);

  const projectIdHintStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (projectIdTrimmed.length > 0 && !isProjectIdFormatValid) {
      return { color: '#FF0000' };
    }
    if (projectIdStatusMessage) {
      return { color: projectIdStatusColor };
    }
    return undefined;
  }, [isProjectIdFormatValid, projectIdStatusColor, projectIdStatusMessage, projectIdTrimmed.length]);

  const runProjectIdCheck = useCallback(
    async (targetProjectId: string) => {
      const id = targetProjectId.trim();
      if (!PROJECT_ID_REGEX.test(id)) return;
      if (
        checkedProjectId === id &&
        (projectIdCheckState === 'available' || projectIdCheckState === 'taken' || projectIdCheckState === 'checking')
      ) {
        return;
      }

      const requestSeq = projectIdCheckRequestSeqRef.current + 1;
      projectIdCheckRequestSeqRef.current = requestSeq;
      setCheckedProjectId(id);
      setProjectIdCheckState('checking');

      try {
        const response = await dispatch(checkProjectId({ project_id: id })).unwrap();
        if (projectIdCheckRequestSeqRef.current !== requestSeq) return;
        const resultCode = Number((response as any)?.result_code ?? (response as any)?.result_Code ?? -1);
        if (resultCode === 0) {
          setProjectIdCheckState('available');
          return;
        }
        if (resultCode === 63001) {
          setProjectIdCheckState('taken');
          return;
        }
        setProjectIdCheckState('error');
      } catch {
        if (projectIdCheckRequestSeqRef.current !== requestSeq) return;
        setProjectIdCheckState('error');
      }
    },
    [checkedProjectId, dispatch, projectIdCheckState],
  );

  const scheduleProjectIdCheck = useCallback(
    (targetProjectId: string) => {
      if (projectIdCheckTimerRef.current !== null) {
        window.clearTimeout(projectIdCheckTimerRef.current);
      }
      projectIdCheckTimerRef.current = window.setTimeout(() => {
        void runProjectIdCheck(targetProjectId);
      }, 300);
    },
    [runProjectIdCheck],
  );

  const handleTitleFieldFocus = useCallback(() => {
    if (!isProjectIdFormatValid) return;
    scheduleProjectIdCheck(projectIdTrimmed);
  }, [isProjectIdFormatValid, projectIdTrimmed, scheduleProjectIdCheck]);

  const handleProjectIdFieldBlur = useCallback(() => {
    setIdFocused(false);
    if (!isProjectIdFormatValid) return;
    void runProjectIdCheck(projectIdTrimmed);
  }, [isProjectIdFormatValid, projectIdTrimmed, runProjectIdCheck]);

  useEffect(() => {
    if (projectIdCheckTimerRef.current !== null && checkedProjectId !== projectIdTrimmed) {
      window.clearTimeout(projectIdCheckTimerRef.current);
      projectIdCheckTimerRef.current = null;
    }
    if (!projectIdTrimmed) {
      setProjectIdCheckState('idle');
      setCheckedProjectId('');
      return;
    }
    if (checkedProjectId && checkedProjectId !== projectIdTrimmed) {
      setProjectIdCheckState('idle');
      setCheckedProjectId('');
    }
  }, [checkedProjectId, projectIdTrimmed]);

  useEffect(() => {
    return () => {
      if (projectIdCheckTimerRef.current !== null) {
        window.clearTimeout(projectIdCheckTimerRef.current);
      }
    };
  }, []);

  const step1Valid = useMemo(() => {
    const titleOk = titleTrimmed.length >= 4 && titleTrimmed.length <= 40;
    const idOk = isProjectIdFormatValid && isProjectIdAvailable;
    const minGoal = currency === 'KRW' ? 35000 : 35;
    const goalOk = numericGoal >= minGoal;
    return titleOk && idOk && goalOk;
  }, [currency, isProjectIdAvailable, isProjectIdFormatValid, numericGoal, titleTrimmed.length]);

  const canNext = useMemo(() => {
    if (step === 1) return step1Valid;
    if (step === 2) return mediaType === 'image' ? Boolean(mediaFile) : videoUrlValid;
    if (step === 3) return true;
    if (step === 4) return Boolean(membership);
    return false;
  }, [mediaFile, mediaType, membership, step, step1Valid, videoUrlValid]);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreviewUrl((prev) => (prev.startsWith('http') ? prev : ''));
      return;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  // NOTE: RichTextEditor 내부에서 이미지 삽입/미리보기 URL 생성/해제 처리

  const stepKey = `step${step}` as const;
  const leftTitle = t(`page.newProject.steps.${stepKey}.title`);
  const leftDesc = t(`page.newProject.steps.${stepKey}.desc`);

  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as any) : s));
  const goNext = () => setStep((s) => (s < 5 ? ((s + 1) as any) : s));

  const handleCurrencyChange = (next: Currency) => {
    if (next === currency) return;
    setCurrency(next);
    setGoalAmount('');
  };

  const handlePickMedia = () => mediaInputRef.current?.click();
  // const handlePickStory = () => storyInputRef.current?.click();

  const handleMediaTypeChange = (nextType: MediaType) => {
    setMediaType(nextType);
    // 파일 input 값을 비우지 않으면, 이전에 고른 파일과 동일한 파일을 다시 선택할 때 onChange가 발생하지 않음
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const handleMediaChange = (f: File | null) => {
    if (f && !isImageSizeAllowed(f)) {
      alert(t('common.errors.imageTooLarge'));
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }
    setMediaFile(f);
  };

  // NOTE: RichTextEditor가 파일 선택/삽입까지 담당 (Step3에서 input 분리 제거)

  const formatGoal = (raw: string, targetCurrency: Currency) => {
    return formatCurrencyInput(raw, targetCurrency);
  };

  const handleGoalAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart !== selectionEnd) return;
    if (currency === 'KRW' && value.endsWith(t('common.units.won')) && selectionStart === value.length) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'KRW') : '');
    } else if (currency === 'USD' && value.startsWith('$') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'USD') : '');
    }
  };

  const ensureKakaoSdk = useCallback(async () => {
    const kakaoWindow = window as any;

    if (kakaoWindow.Kakao) {
      if (!kakaoWindow.Kakao.isInitialized()) {
        kakaoWindow.Kakao.init(KAKAO_KEY);
      }
      return kakaoWindow.Kakao;
    }

    if (kakaoSdkLoadingRef.current) {
      return kakaoSdkLoadingRef.current;
    }

    kakaoSdkLoadingRef.current = new Promise((resolve, reject) => {
      const finalize = () => {
        if (!kakaoWindow.Kakao) {
          reject(new Error('Kakao SDK not loaded'));
          return;
        }
        if (!kakaoWindow.Kakao.isInitialized()) {
          kakaoWindow.Kakao.init(KAKAO_KEY);
        }
        resolve(kakaoWindow.Kakao);
      };

      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${KAKAO_SDK_SRC}"]`);
      if (existingScript) {
        if (kakaoWindow.Kakao) {
          finalize();
          return;
        }
        existingScript.addEventListener('load', finalize, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = KAKAO_SDK_SRC;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', finalize, { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      kakaoSdkLoadingRef.current = null;
      throw error;
    });

    return kakaoSdkLoadingRef.current;
  }, []);

  const handleShareLink = () => {
    setIsShareModalOpen(true);
    void ensureKakaoSdk().catch(() => {}); // SDK 미리 로드 (모바일 사용자 제스처 보존용)
  };

  useEffect(() => {
    void ensureKakaoSdk().catch(() => {});
  }, [ensureKakaoSdk]);

  const runKakaoShare = async () => {
    try {
      const kakao = await ensureKakaoSdk();
      shareToKakao(kakao);
    } catch (error) {
      console.error('Failed to run Kakao share:', error);
      // eslint-disable-next-line no-alert
      alert(t('common.messages.kakaoShareLoadFailed'));
    }
  };

  const shareToKakao = (kakao: any) => {
    const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
    const pid = (createdProjectId || projectIdTrimmed).trim();
    const fromStore =
      selectedProject?.project_id === pid
        ? { image_url: selectedProject.image_url, video_url: selectedProject.video_url }
        : undefined;
    const imageUrl = getProjectShareImageUrl(
      fromStore ?? (mediaType === 'video' ? { video_url: videoUrlTrimmed } : undefined),
    );
    shareKakaoFeed(kakao, {
      url: projectUrl,
      title: titleTrimmed || t('common.shareModal.defaultTitle'),
      imageUrl,
      buttonTitle: t('common.shareModal.buttonDetail'),
      fundraiserName: userFullName || t('common.share.fundraiserFallback'),
      restrictedRegion,
    });
  };

  const runEmailShare = () => {
    const { subject, body } = buildProjectShareMailTemplate({
      fundraiserName: userFullName,
      projectName: titleTrimmed,
      projectUrl,
      defaultProjectName: t('common.shareModal.defaultTitle'),
    });
    openMailClient({ subject, body });
  };

  const handleKakaoShare = () => {
    setIsShareModalOpen(false);
    runKakaoShare(); // 사용자 클릭 직후 즉시 실행 (모바일 제스처 보존)
  };

  const handleEmailShare = () => {
    setIsShareModalOpen(false);
    runEmailShare();
  };

  const handleCopyShareLink = async () => {
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = projectUrl;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(ta);
      return copied;
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(projectUrl);
        alert(t('common.messages.linkCopied'));
        return;
      }
    } catch {
      // fallback below
    }

    if (fallbackCopy()) {
      alert(t('common.messages.linkCopied'));
      return;
    }

    alert(t('page.newProject.errors.linkCopyFailed'));
  };

  const handleOpenPage = () => {
    window.open(projectUrl, '_blank', 'noopener,noreferrer');
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error(t('page.newProject.errors.imageReadFailed')));
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.readAsDataURL(file);
    });
  };

  const handleEditorImageUpload = (file: File): Promise<string> => {
    if (!isImageSizeAllowed(file)) {
      alert(t('common.errors.imageTooLarge'));
      return Promise.reject(new Error('image too large'));
    }
    return Promise.resolve(URL.createObjectURL(file));
  };

  const membershipMissionAccountNo = useMemo(() => {
    if (membership === 'partner') return 'PARTNER';
    if (membership === 'builder') return 'Builder';
    if (membership === 'prime') return 'Prime';
    return '';
  }, [membership]);

  const handleCreate = async () => {
    if (creating) return;

    // minimal guards
    if (!userId) {
      // eslint-disable-next-line no-alert
      alert(t('page.newProject.errors.loginRequired'));
      setPreviewOpen(false);
      navigate(PATHS.LOGIN);
      return;
    }
    if (!projectIdTrimmed || !titleTrimmed) {
      // eslint-disable-next-line no-alert
      alert(t('page.newProject.errors.missingProjectInfo'));
      return;
    }

    setCreating(true);
    try {
      // 1. Parse storyHtml for any local (blob:) images and upload them
      let updatedStoryHtml = storyHtml;
      const blobUrls = updatedStoryHtml.match(/src="blob:[^"]+"/g) || [];

      const partner = String((process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF');

      for (const match of blobUrls) {
        const url = match.slice(5, -1);
        try {
          const resBlob = await fetch(url);
          const blob = await resBlob.blob();
          const dataUrl = await readFileAsBase64(new File([blob], 'image.png', { type: blob.type }));
          const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

          const uploadRes = await dispatch(
            uploadProjectImage({
              partner,
              project_id: projectIdTrimmed,
              projectpost_id: '',
              language_code: 'ko',
              isuse: true,
              image: base64,
            }),
          ).unwrap();

          if (uploadRes.image_url) {
            updatedStoryHtml = updatedStoryHtml.replace(url, uploadRes.image_url);
          }
        } catch (uploadErr) {
          console.error('Additional image upload failed:', uploadErr);
          // continue anyway
        }
      }

      updatedStoryHtml = sanitizeProjectDescHtml(updatedStoryHtml);

      // 2. Representative image (base64 string without prefix)
      let imageBase64 = '';
      if (mediaType === 'image' && mediaFile) {
        const dataUrl = await readFileAsBase64(mediaFile);
        imageBase64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      }

      const type = fundingType === 'once' ? 'OT' : 'RT';
      const youtubeThumbnail = mediaType === 'video' ? getYouTubeThumbnail(videoUrlTrimmed) ?? '' : '';

      let membershipValue = 'Basic';
      let supportPercent = '0';

      if (membership === 'partner') {
        membershipValue = 'Partner';
        supportPercent = '3';
      } else if (membership === 'builder') {
        membershipValue = 'Builder';
        supportPercent = '5';
      } else if (membership === 'prime') {
        membershipValue = 'Prime';
        supportPercent = '10';
      }

      const res = await dispatch(
        createProject({
          project_id: projectIdTrimmed,
          partner,
          title: titleTrimmed,
          project_desc: updatedStoryHtml || t('page.newProject.placeholders.projectDesc'),
          is_image_display: mediaType !== 'video',
          image: imageBase64,
          image_url: mediaType === 'video' ? youtubeThumbnail : '',
          video_url: mediaType === 'video' ? videoUrlTrimmed : '',
          user_id: userId,
          user_full_name: userFullName || 'user',
          type,
          start_date: new Date().toISOString().slice(0, 10),
          end_date: '',
          goal_amount: String(numericGoal || 0),
          currency_type: currency,
          is_recommended: false,
          recommended_organization_name: '',
          is_support: false,
          support_organization_name: '',
          status: 'AC',
          comment: '',
          campaign_code: '',
          category_code: '',
          group_account_status: '',
          is_verified: false,
          mission_account_no: membershipMissionAccountNo,
          membership: membershipValue,
          support_project: {
            project_id: 'wmf',
            image_url: 'https://mfimage.s3.amazonaws.com/OMF/projects/6926946aa11b995e56a08516_1764136042390.png',
            project_desc: t('page.newProject.sample.story'),
            support_orgnaization: '',
            support_parnter: 'OMF',
            support_percent: supportPercent,
            title: t('page.newProject.sample.title'),
            user_full_name: 'Drone Team',
            user_id: '10B896ED',
            video_url: '',
          },
        }),
      ).unwrap();

      const insertedId = (res as any)?.result_message?.insertedId;
      const finalProjectId = typeof insertedId === 'string' && insertedId.trim() ? insertedId : projectIdTrimmed;
      setCreatedProjectId(finalProjectId);

      try {
        await dispatch(getProject({ project_id: finalProjectId })).unwrap();
      } catch {
        // 공유 썸네일은 폴백·유튜브 썸네일로 동작. 완료 UI는 그대로 진행
      }

      setPreviewOpen(false);
      setCompleteOpen(true);
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(e?.message || (typeof e === 'string' ? e : t('page.newProject.errors.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const requestExitToProjectManagement = () => {
    if (hasDraftChanges) {
      setExitConfirmOpen(true);
      return;
    }
    navigate(PATHS.PROJECT_MANAGEMENT);
  };

  return (
    <PageBackground>
      <DSModal
        open={exitConfirmOpen}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmOpen(false)}
        actions={[
          {
            label: t('common.exitConfirm.leave'),
            variant: 'secondary',
            onClick: () => {
              setExitConfirmOpen(false);
              navigate(PATHS.PROJECT_MANAGEMENT);
            },
          },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmOpen(false) },
        ]}
      />
      <Main>
        <Content>
          <Wrap $isStep3={step === 3}>
            <Left $isStep3={step === 3}>
              <BrandPill type="button" onClick={requestExitToProjectManagement} aria-label={t('page.newProject.actions.goToProjects')}>
                <BrandLogo src={logoImg} alt="Mission Fund" />
                <HomeIcon src={homeIconImg} alt="home" />
              </BrandPill>

              <Stepper $isStep3={step === 3}>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <React.Fragment key={n}>
                    <StepDot $active={n === step}>{n}</StepDot>
                    {n !== 5 ? <StepLine /> : null}
                  </React.Fragment>
                ))}
              </Stepper>

              <LeftTitle>{leftTitle}</LeftTitle>
              <LeftDesc>{leftDesc}</LeftDesc>

              {step === 3 ? (
                <TipsWrap $isStep3={step === 3}>
                  <TipsTitle>{t('page.newProject.tips.title')}</TipsTitle>
                  <TipsDesc>{t('page.newProject.tips.description')}</TipsDesc>
                  <TipsGrid>
                    <TipCard>
                      <TipHead>
                        <TipNum>1</TipNum>
                        <TipName>{t('page.newProject.tips.items.intro.title')}</TipName>
                      </TipHead>
                      <TipText>{t('page.newProject.tips.items.intro.desc')}</TipText>
                    </TipCard>
                    <TipCard>
                      <TipHead>
                        <TipNum>2</TipNum>
                        <TipName>{t('page.newProject.tips.items.mission.title')}</TipName>
                      </TipHead>
                      <TipText>{t('page.newProject.tips.items.mission.desc')}</TipText>
                    </TipCard>
                    <TipCard>
                      <TipHead>
                        <TipNum>3</TipNum>
                        <TipName>{t('page.newProject.tips.items.needs.title')}</TipName>
                      </TipHead>
                      <TipText>{t('page.newProject.tips.items.needs.desc')}</TipText>
                    </TipCard>
                    <TipCard>
                      <TipHead>
                        <TipNum>4</TipNum>
                        <TipName>{t('page.newProject.tips.items.plan.title')}</TipName>
                      </TipHead>
                      <TipText>{t('page.newProject.tips.items.plan.desc')}</TipText>
                    </TipCard>
                    <TipCard>
                      <TipHead>
                        <TipNum>5</TipNum>
                        <TipName>{t('page.newProject.tips.items.updates.title')}</TipName>
                      </TipHead>
                      <TipText>{t('page.newProject.tips.items.updates.desc')}</TipText>
                    </TipCard>
                  </TipsGrid>
                </TipsWrap>
              ) : null}
            </Left>

            <Right>
              <Card>
                <CardBody>
                  {step === 1 ? (
                    <>
                      <FieldTitle>{t('page.newProject.sections.fundingType')}</FieldTitle>
                      <ChoiceRow>
                        <ChoiceCard
                          type="button"
                          $active={fundingType === 'regular'}
                          onClick={() => setFundingType('regular')}
                        >
                          <ChoiceTop $active={fundingType === 'regular'}>{t('page.projectManagement.fundingType.recurring')}</ChoiceTop>
                          <ChoiceSub $active={fundingType === 'regular'}>{t('page.newProject.fundingTypeDescriptions.regular')}</ChoiceSub>
                        </ChoiceCard>
                        <ChoiceCard type="button" $active={fundingType === 'once'} onClick={() => setFundingType('once')}>
                          <ChoiceTop $active={fundingType === 'once'}>{t('page.projectManagement.fundingType.oneTime')}</ChoiceTop>
                          <ChoiceSub $active={fundingType === 'once'}>{t('page.newProject.fundingTypeDescriptions.once')}</ChoiceSub>
                        </ChoiceCard>
                      </ChoiceRow>

                      <FieldTitle style={{ marginTop: 24 }}>{t('page.projectSettings.sections.goalAmount')}</FieldTitle>
                      <RadioRow>
                        <RadioBtn type="button" $active={currency === 'KRW'} onClick={() => handleCurrencyChange('KRW')}>
                          <RadioDot $active={currency === 'KRW'} />
                          {t('common.currency.krw')}
                        </RadioBtn>
                        <RadioBtn type="button" $active={currency === 'USD'} onClick={() => handleCurrencyChange('USD')}>
                          <RadioDot $active={currency === 'USD'} />
                          {t('common.currency.usd')}
                        </RadioBtn>
                      </RadioRow>
                      <GoalUnderlineInput
                        id="goal-input"
                        placeholder={t('page.projectSettings.placeholders.goalAmount')}
                        value={goalAmount}
                        onChange={(e) => setGoalAmount(formatGoal(e.target.value, currency))}
                        onKeyDown={handleGoalAmountKeyDown}
                        inputMode="numeric"
                        $error={goalAmount.trim() !== '' && numericGoal < (currency === 'KRW' ? 35000 : 35)}
                      />
                      {showGoalPreview ? <GoalPreview>{goalPreviewText}</GoalPreview> : null}
                      <HintRow>
                        {goalAmount.trim() !== '' && numericGoal < (currency === 'KRW' ? 35000 : 35) ? (
                          <HintText style={{ color: '#FF0000', marginTop: '4px' }}>{currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}</HintText>
                        ) : (
                          <HintText style={{ marginTop: '4px' }}>{currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}</HintText>
                        )}
                      </HintRow>

                      <FieldTitle style={{ marginTop: 24 }}>{t('page.newProject.sections.projectId')}</FieldTitle>
                      <InputBox
                        $active={idFocused}
                        $error={(projectIdTrimmed.length > 0 && !isProjectIdFormatValid) || isProjectIdTaken}
                        onClick={() => document.getElementById('id-input')?.focus()}
                      >
                        {(projectId || idFocused) && <InputLabel>{t('page.newProject.placeholders.projectId')}</InputLabel>}
                        <StyledInputField
                          id="id-input"
                          placeholder={!idFocused ? t('page.newProject.placeholders.projectId') : ''}
                          value={projectId}
                          onChange={(e) => setProjectId(sanitizeProjectIdInput(e.target.value))}
                          onFocus={() => setIdFocused(true)}
                          onBlur={handleProjectIdFieldBlur}
                          $error={(projectIdTrimmed.length > 0 && !isProjectIdFormatValid) || isProjectIdTaken}
                        />
                      </InputBox>
                      <HintText style={projectIdHintStyle}>
                        {projectIdStatusMessage || t('page.newProject.helpers.projectId')}
                      </HintText>
                      <UrlPreview>
                        <UrlLabel>{t('page.newProject.labels.urlPreview')}</UrlLabel>
                        <UrlValue>
                          https://go.MissionFund.org/
                          <UrlValueHighlight>{projectIdTrimmed || ''}</UrlValueHighlight>
                        </UrlValue>
                      </UrlPreview>

                      <FieldTitle style={{ marginTop: 24 }}>{t('page.projectSettings.sections.projectTitle')}</FieldTitle>
                      <InputBox
                        $active={titleFocused}
                        $error={titleTrimmed.length > 0 && (titleTrimmed.length < 4 || titleTrimmed.length > 40)}
                        style={{ height: 'auto', minHeight: '120px', width: '100%', maxWidth: 562 }}
                        onClick={() => {
                          handleTitleFieldFocus();
                          document.getElementById('title-input')?.focus();
                        }}
                      >
                        {(title || titleFocused) && <InputLabel>{t('page.projectSettings.placeholders.projectTitle')}</InputLabel>}
                        <StyledTextAreaField
                          id="title-input"
                          placeholder={!titleFocused ? t('page.projectSettings.placeholders.projectTitle') : ''}
                          value={title}
                          onChange={(e) => setTitle(sanitizeProjectTitle(e.target.value).slice(0, 40))}
                          rows={3}
                          onFocus={() => {
                            setTitleFocused(true);
                            handleTitleFieldFocus();
                          }}
                          onBlur={() => setTitleFocused(false)}
                          $error={titleTrimmed.length > 0 && (titleTrimmed.length < 4 || titleTrimmed.length > 40)}
                        />
                      </InputBox>
                      <HintText>{t('page.projectSettings.helpers.titleLengthShort')}</HintText>
                    </>
                  ) : null}

                  {step === 2 ? (
                    <>
                      <MediaTitle>{t('page.postManagement.modal.mediaSectionTitle')}</MediaTitle>
                      <MediaTypeRow>
                        <MediaTypeRadioBtn type="button" $active={mediaType === 'image'} onClick={() => handleMediaTypeChange('image')}>
                          <MediaTypeRadioDot $active={mediaType === 'image'} />
                          {t('page.postManagement.modal.mediaType.image')}
                        </MediaTypeRadioBtn>
                        <MediaTypeRadioBtn type="button" $active={mediaType === 'video'} onClick={() => handleMediaTypeChange('video')}>
                          <MediaTypeRadioDot $active={mediaType === 'video'} />
                          {t('page.postManagement.modal.mediaType.video')}
                        </MediaTypeRadioBtn>
                      </MediaTypeRow>

                      {mediaType === 'video' ? (
                        <>
                          <VideoUrlRow>
                            <VideoUrlField
                              $state={showVideoUrlError ? 'error' : videoUrlFocused ? 'focus' : videoUrlTrimmed ? 'complete' : 'default'}
                            >
                              <VideoUrlText>
                                {(videoUrlFocused || videoUrlTrimmed) && <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>}
                                <VideoUrlInput
                                  placeholder={videoUrlFocused || videoUrlTrimmed ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                                  value={videoUrl}
                                  onChange={(e) => {
                                    setVideoUrl(e.target.value);
                                  }}
                                  onFocus={() => setVideoUrlFocused(true)}
                                  onBlur={() => setVideoUrlFocused(false)}
                                />
                              </VideoUrlText>
                              {videoUrlTrimmed ? (
                                <VideoUrlClear
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setVideoUrl('');
                                  }}
                                  aria-label="clear"
                                >
                                  ×
                                </VideoUrlClear>
                              ) : null}
                            </VideoUrlField>
                          </VideoUrlRow>
                          {showVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                        </>
                      ) : (
                        <UploadRow>
                          <UploadHint $disabled={false}>
                            {t('page.postManagement.modal.uploadHintImage')}
                          </UploadHint>
                          <UploadBtn type="button" onClick={handlePickMedia} disabled={false}>
                            {t('common.actions.attach')}
                          </UploadBtn>
                        </UploadRow>
                      )}
                      <input
                        ref={mediaInputRef}
                        type="file"
                        accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                        style={{ display: 'none' }}
                        onChange={(e) => handleMediaChange(e.target.files?.[0] ?? null)}
                        disabled={false}
                      />

                      <MediaPreview>
                        {mediaType === 'video' && videoPreviewSrc ? (
                          <MediaPreviewIframe
                            src={videoPreviewSrc}
                            title="video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : mediaType === 'video' && mediaPreviewUrl ? (
                          <MediaPreviewVideo src={mediaPreviewUrl} controls />
                        ) : mediaType === 'video' ? (
                          <MediaPreviewEmptyWrap>
                            <MediaPreviewEmpty>{t('page.projectSettings.media.linkPreviewHint')}</MediaPreviewEmpty>
                            <MediaPreviewVideoIcon
                              xmlns="http://www.w3.org/2000/svg"
                              width="70"
                              height="70"
                              viewBox="0 0 70 70"
                              fill="none"
                              aria-hidden
                              focusable="false"
                            >
                              <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                              <path d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </MediaPreviewVideoIcon>
                          </MediaPreviewEmptyWrap>
                        ) : mediaPreviewUrl ? (
                          <Step2MediaPreviewImg src={mediaPreviewUrl} alt="" />
                        ) : (
                          <MediaPreviewEmptyWrap>
                            <MediaPreviewEmptyText>{t('page.postManagement.modal.previewHintImage')}</MediaPreviewEmptyText>
                            <MediaPreviewIcon src={pictureIconImg} alt="preview" />
                          </MediaPreviewEmptyWrap>
                        )}
                      </MediaPreview>
                    </>
                  ) : null}

                  {step === 3 ? (
                    <>
                      <FieldTitle>{t('page.projectSettings.modal.storyDescLine1')}</FieldTitle>
                      <HintText>{t('page.projectSettings.modal.storyDescLine2')}</HintText>

                      <Step3Scroll ref={step3ScrollRef}>
                        <RichTextEditor
                          ref={richTextEditorRef}
                          value={storyHtml}
                          onChange={setStoryHtml}
                          placeholder={t('page.projectSettings.modal.storyPlaceholder')}
                          pictureIconSrc={pictureIconImg}
                          maxImages={3}
                          imageCount={totalStoryPhotoCount}
                          pinnedImageSrc={hasRepresentativeImage ? mediaPreviewUrl : undefined}
                          pinnedVideoEmbedUrl={mediaType === 'video' && videoPreviewSrc ? videoPreviewSrc : undefined}
                          pinnedBadgeLabel={t('common.labels.main')}
                          showToolbar={false}
                          previewHeightPx={240}
                          onImageUpload={handleEditorImageUpload}
                          onFocus={() => {
                            requestAnimationFrame(() => {
                              scrollStep3StoryEditorIntoView();
                            });
                          }}
                          onImageInserted={() => {
                            requestAnimationFrame(() => {
                              richTextEditorRef.current?.focus();
                              requestAnimationFrame(() => {
                                scrollStep3StoryEditorIntoView();
                        requestAnimationFrame(() => {
                          richTextEditorRef.current?.focus();
                        });
                              });
                            });
                          }}
                        />
                      </Step3Scroll>

                      <Step3AddPhotoBtn
                        type="button"
                        onClick={() => {
                          richTextEditorRef.current?.openImagePicker();
                        }}
                        disabled={totalStoryPhotoCount >= 3}
                      >
                        <Step3AddPhotoIcon src={pictureIconImg} alt="" />
                        {t('page.postManagement.modal.addPhoto', { count: totalStoryPhotoCount })}
                      </Step3AddPhotoBtn>
                    </>
                  ) : null}

                  {step === 4 ? (
                    <>
                      <FieldTitle>{t('page.newProject.sections.membership')}</FieldTitle>
                      <MembershipScroll>
                        <MembershipList>
                          <MembershipCard type="button" $active={membership === 'prime'} onClick={() => setMembership('prime')}>
                            <MembershipHeader>
                              <RadioDot $active={membership === 'prime'} />
                              <MembershipName $active={membership === 'prime'}>{t('page.newProject.membership.prime.name')}</MembershipName>
                            </MembershipHeader>
                            <MembershipDesc>{t('page.newProject.membership.prime.desc')}</MembershipDesc>
                            <MembershipChecks>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.prime.benefits.allBuilder')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.prime.benefits.designSupport')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.prime.benefits.kakaoSystem')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.prime.benefits.topExposure')}
                              </MembershipCheckRow>
                            </MembershipChecks>
                          </MembershipCard>

                          <MembershipCard type="button" $active={membership === 'builder'} onClick={() => setMembership('builder')}>
                            <MembershipHeader>
                              <RadioDot $active={membership === 'builder'} />
                              <MembershipName $active={membership === 'builder'}>{t('page.newProject.membership.builder.name')}</MembershipName>
                            </MembershipHeader>
                            <MembershipDesc>{t('page.newProject.membership.builder.desc')}</MembershipDesc>
                            <MembershipChecks>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.builder.benefits.allPartner')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.builder.benefits.exposure')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.builder.benefits.prioritySupport')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.builder.benefits.events')}
                              </MembershipCheckRow>
                            </MembershipChecks>
                          </MembershipCard>

                          <MembershipCard
                            type="button"
                            $active={membership === 'partner'}
                            onClick={() => setMembership('partner')}
                          >
                            <MembershipHeader>
                              <RadioDot $active={membership === 'partner'} />
                              <MembershipName $active={membership === 'partner'}>{t('page.newProject.membership.partner.name')}</MembershipName>
                            </MembershipHeader>
                            <MembershipDesc>{t('page.newProject.membership.partner.desc')}</MembershipDesc>
                            <MembershipChecks>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.partner.benefits.journal')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.partner.benefits.privateFunding')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.partner.benefits.paymentRecovery')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.partner.benefits.thanksCard')}
                              </MembershipCheckRow>
                            </MembershipChecks>
                          </MembershipCard>

                          <MembershipCard
                            type="button"
                            $active={membership === 'general'}
                            onClick={() => setMembership('general')}
                          >
                            <MembershipHeader>
                              <RadioDot $active={membership === 'general'} />
                              <MembershipName $active={membership === 'general'}>{t('page.newProject.membership.general.name')}</MembershipName>
                            </MembershipHeader>
                            <MembershipDesc>{t('page.newProject.membership.general.desc')}</MembershipDesc>
                            <MembershipChecks>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.general.benefits.startFunding')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.general.benefits.basicPage')}
                              </MembershipCheckRow>
                              <MembershipCheckRow>
                                <CheckIcon src={checkIconImg} alt="" /> {t('page.newProject.membership.general.benefits.changeable')}
                              </MembershipCheckRow>
                            </MembershipChecks>
                          </MembershipCard>
                        </MembershipList>
                      </MembershipScroll>
                    </>
                  ) : null}

                  {step === 5 ? (
                    <Step5Body>
                      <ReviewTopTitle>{t('page.newProject.review.title')}</ReviewTopTitle>
                      <Step5Scroll>
                        <ReviewSectionBox>
                          <ReviewSectionHeader>
                            <ReviewSectionTitle>{t('page.newProject.review.basic.title')}</ReviewSectionTitle>
                            <ReviewEditBtn type="button" onClick={() => setStep(1)}>
                              {t('page.projectSettings.actions.edit')}
                            </ReviewEditBtn>
                          </ReviewSectionHeader>

                          <ReviewRows>
                            <ReviewRowVertical>
                              <ReviewRowLabel>{t('page.newProject.review.basic.fundingType')}</ReviewRowLabel>
                              <ReviewRowValue>{fundingType === 'regular' ? t('page.newProject.review.fundingType.regular') : t('page.newProject.review.fundingType.once')}</ReviewRowValue>
                            </ReviewRowVertical>
                            <ReviewRowVertical>
                              <ReviewRowLabel>{t('page.projectSettings.labels.goalAmount')}</ReviewRowLabel>
                              <ReviewRowValue>
                                {numericGoal.toLocaleString('ko-KR')}
                                {currency === 'KRW' ? t('common.units.won') : '$'}
                              </ReviewRowValue>
                            </ReviewRowVertical>
                            <ReviewRowVerticalFull>
                              <ReviewRowLabel>{t('page.projectSettings.labels.projectTitle')}</ReviewRowLabel>
                              <ReviewRowTitleValue>{titleTrimmed || '-'}</ReviewRowTitleValue>
                            </ReviewRowVerticalFull>
                          </ReviewRows>

                          <ReviewRowVertical style={{ marginTop: '20px' }}>
                            <ReviewRowLabel>{t('page.newProject.labels.projectUrl')}</ReviewRowLabel>
                            <ReviewRowUrlValue>{`https://go.MissionFund.org/${projectIdTrimmed || 'yourID'}`}</ReviewRowUrlValue>
                          </ReviewRowVertical>
                        </ReviewSectionBox>

                        <ReviewDivider />

                        <ReviewSectionBox>
                          <ReviewSectionHeader>
                            <ReviewSectionTitle>{t('page.projectSettings.sections.media')}</ReviewSectionTitle>
                            <ReviewEditBtn type="button" onClick={() => setStep(2)}>
                              {t('page.projectSettings.actions.edit')}
                            </ReviewEditBtn>
                          </ReviewSectionHeader>

                          <ReviewMediaPreview>
                            {mediaType === 'video' && videoPreviewSrc ? (
                              <ReviewMediaIframe
                                src={videoPreviewSrc}
                                title="video preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            ) : mediaType === 'video' && mediaPreviewUrl ? (
                              <ReviewMediaVideo src={mediaPreviewUrl} controls />
                            ) : mediaPreviewUrl ? (
                              <ReviewMediaImg src={mediaPreviewUrl} alt="" />
                            ) : (
                              <Step2MediaPreviewEmpty>{t('page.postManagement.modal.mediaType.none')}</Step2MediaPreviewEmpty>
                            )}
                            {mediaType === 'image' && mediaPreviewUrl ? <ReviewMediaBadge>{t('page.postManagement.modal.mainBadge')}</ReviewMediaBadge> : null}
                          </ReviewMediaPreview>
                        </ReviewSectionBox>

                        <ReviewDivider />

                        <ReviewSectionBox>
                          <ReviewSectionHeader>
                            <ReviewSectionTitle>{t('page.projectSettings.sections.story')}</ReviewSectionTitle>
                            <ReviewEditBtn type="button" onClick={() => setStep(3)}>
                              {t('page.projectSettings.actions.edit')}
                            </ReviewEditBtn>
                          </ReviewSectionHeader>
                          <ReviewStoryBox>
                            <ReviewStoryContent $expanded={isStep5StoryExpanded}>
                              {storyHtml ? (
                                <ReviewStoryHtml dangerouslySetInnerHTML={{ __html: storyHtml }} />
                              ) : (
                                <ReviewStoryEmpty>{t('page.newProject.review.storyEmpty')}</ReviewStoryEmpty>
                              )}
                            </ReviewStoryContent>
                            {storyHtml ? (
                              <ReviewStoryToggleWrap>
                                <ReviewStoryToggle type="button" onClick={() => setIsStep5StoryExpanded((prev) => !prev)}>
                                  {isStep5StoryExpanded
                                    ? t('page.newProject.review.storyToggleCollapse')
                                    : t('page.newProject.review.storyToggleExpand')}
                                  <ReviewStoryToggleIcon
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 13 8"
                                    $expanded={isStep5StoryExpanded}
                                    aria-hidden
                                    focusable="false"
                                  >
                                    <path
                                      d="M1.06022 8.28276e-05L0.000215603 1.06108L5.77722 6.84008C5.86978 6.93324 5.97986 7.00717 6.10111 7.05761C6.22236 7.10806 6.35239 7.13403 6.48372 7.13403C6.61504 7.13403 6.74507 7.10806 6.86632 7.05761C6.98757 7.00717 7.09765 6.93324 7.19021 6.84008L12.9702 1.06108L11.9102 0.00108288L6.48521 5.42508L1.06022 8.28276e-05Z"
                                      fill="#676767"
                                    />
                                  </ReviewStoryToggleIcon>
                                </ReviewStoryToggle>
                              </ReviewStoryToggleWrap>
                            ) : null}
                          </ReviewStoryBox>
                        </ReviewSectionBox>

                        <ReviewDivider />

                        <ReviewSectionBox>
                          <ReviewSectionHeader>
                            <ReviewSectionTitle>{t('page.newProject.sections.membership')}</ReviewSectionTitle>
                          </ReviewSectionHeader>
                          <ReviewMembershipName>
                            {membership === 'partner'
                              ? t('page.newProject.membership.partner.name')
                              : membership === 'builder'
                                ? t('page.newProject.membership.builder.name')
                                : membership === 'prime'
                                  ? t('page.newProject.membership.prime.name')
                                  : t('page.newProject.membership.general.name')}
                          </ReviewMembershipName>
                          <ReviewMembershipDesc>
                            {membership === 'partner'
                              ? t('page.newProject.membership.partner.desc')
                              : membership === 'builder'
                                ? t('page.newProject.membership.builder.desc')
                                : membership === 'prime'
                                  ? t('page.newProject.membership.prime.desc')
                                  : t('page.newProject.membership.general.desc')}
                          </ReviewMembershipDesc>
                        </ReviewSectionBox>
                      </Step5Scroll>
                    </Step5Body>
                  ) : null}
                </CardBody>

                <CardFooter>
                  <FooterRight>
                    {step > 1 ? (
                      <FooterBtn type="button" $variant="secondary" onClick={goBack}>
                        {t('common.actions.back')}
                      </FooterBtn>
                    ) : null}

                    {step < 5 ? (
                      <FooterBtn type="button" $variant="primary" onClick={goNext} disabled={!canNext}>
                        {t('common.actions.next')}
                      </FooterBtn>
                    ) : (
                      <>
                        <FooterBtn
                          type="button"
                          $variant="primary"
                          onClick={() => {
                            setPreviewOpen(true);
                          }}
                        >
                          {t('common.actions.confirm')}
                        </FooterBtn>
                      </>
                    )}
                  </FooterRight>
                </CardFooter>
              </Card>
            </Right>
          </Wrap>

          {previewOpen ? (
            <PreviewOverlay
              role="dialog"
              aria-modal="true"
              onPointerDown={() => {
                setPreviewOpen(false);
              }}
            >
              {isGeneralMembership ? (
                <PreviewGeneral
                  key="preview-general"
                  title={titleTrimmed}
                  projectId={projectIdTrimmed}
                  goalAmount={goalAmount}
                  currency={currency}
                  mediaPreviewUrl={mediaPreviewUrl}
                  storyHtml={storyHtml}
                  userFullName={userFullName}
                  onClose={() => setPreviewOpen(false)}
                  onCreate={handleCreate}
                  creating={creating}
                  creatingLabel={creatingLabel}
                  videoPreviewSrc={videoPreviewSrc}
                />
              ) : (
                <PreviewPartner
                  key={`preview-${normalizedMembership}`}
                  title={titleTrimmed}
                  projectId={projectIdTrimmed}
                  goalAmount={goalAmount}
                  currency={currency}
                  mediaPreviewUrl={mediaPreviewUrl}
                  storyHtml={storyHtml}
                  userFullName={userFullName}
                  membership={normalizedMembership}
                  onClose={() => setPreviewOpen(false)}
                  onCreate={handleCreate}
                  creating={creating}
                  creatingLabel={creatingLabel}
                  videoPreviewSrc={videoPreviewSrc}
                />
              )}
            </PreviewOverlay>
          ) : null}

          {completeOpen ? (
            <CompleteOverlay
              role="dialog"
              aria-modal="true"
              aria-label={t('page.newProject.complete.ariaLabel')}
              onPointerDown={() => {
                // backdrop click: do nothing (force explicit action)
              }}
            >
              <CompleteDialog
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
              >
                <CompleteLogo src={logoImg} alt="Mission Fund" />

                <CompleteLottieWrap aria-hidden>
                  <DotLottieReact src={successCheckLottie} autoplay loop style={{ width: '100%', height: '100%' }} />
                </CompleteLottieWrap>

                <CompleteTitle>{t('page.newProject.complete.title')}</CompleteTitle>

                <CompleteButtons>
                  {/* TODO: 생성 완료 시 실제 project id 보장되면 disabled 조건 복원 */}
                  <CompleteButton type="button" onClick={handleShareLink}>
                    {t('page.newProject.complete.shareLink')}
                  </CompleteButton>
                  {/* TODO: 생성 완료 시 실제 project id 보장되면 disabled 조건 복원 */}
                  <CompleteButton type="button" onClick={handleOpenPage}>
                    {t('page.newProject.complete.openPage')}
                  </CompleteButton>
                </CompleteButtons>

                <SkipButton
                  type="button"
                  onClick={() => {
                    setIsShareModalOpen(false);
                    setCompleteOpen(false);
                    window.location.href = PATHS.PROJECT_MANAGEMENT;
                  }}
                >
                  {t('page.newProject.complete.skip')}
                </SkipButton>
              </CompleteDialog>
              {isShareModalOpen ? (
                <ShareModalOverlay onPointerDown={() => setIsShareModalOpen(false)}>
                  <ShareModalCard onPointerDown={(e) => e.stopPropagation()}>
                    <ShareModalHeader>
                      <ShareModalTitleWrap>
                        <ShareModalTitle>{t('page.newProject.shareModal.title')}</ShareModalTitle>
                        <ShareModalDesc>
                          {t('common.shareModal.descLine1')} {t('common.shareModal.descLine2')}
                        </ShareModalDesc>
                      </ShareModalTitleWrap>
                      <ShareModalCloseButton
                        type="button"
                        onClick={() => setIsShareModalOpen(false)}
                        aria-label={t('common.actions.close')}
                      >
                        <ShareModalCloseIcon
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          aria-hidden
                          focusable="false"
                        >
                          <path fillRule="evenodd" clipRule="evenodd" d="M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z" fill="#929292" />
                        </ShareModalCloseIcon>
                      </ShareModalCloseButton>
                    </ShareModalHeader>
                    <ShareModalBody>
                      <ShareOption onClick={handleKakaoShare}>
                        <ShareOptionIconKakao>
                          <img src={kakaoIconImg} alt={t('common.shareModal.kakaoAlt')} />
                        </ShareOptionIconKakao>
                        <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
                      </ShareOption>
                      <ShareOption onClick={handleEmailShare}>
                        <ShareOptionIconEmail>
                          <ShareEmailIcon
                            xmlns="http://www.w3.org/2000/svg"
                            width="30"
                            height="24"
                            viewBox="0 0 30 24"
                            fill="none"
                            aria-hidden
                            focusable="false"
                          >
                            <path d="M27 0H3C1.35 0 0.015 1.35 0.015 3L0 21C0 22.65 1.35 24 3 24H27C28.65 24 30 22.65 30 21V3C30 1.35 28.65 0 27 0ZM26.4 6.375L15.795 13.005C15.315 13.305 14.685 13.305 14.205 13.005L3.6 6.375C3.44959 6.29057 3.31788 6.17649 3.21283 6.03968C3.10779 5.90287 3.03159 5.74617 2.98886 5.57906C2.94613 5.41195 2.93775 5.2379 2.96423 5.06746C2.99072 4.89702 3.0515 4.73372 3.14292 4.58745C3.23434 4.44118 3.35449 4.31498 3.49609 4.21649C3.63769 4.118 3.79781 4.04927 3.96674 4.01445C4.13568 3.97963 4.30992 3.97945 4.47893 4.01392C4.64794 4.04839 4.8082 4.1168 4.95 4.215L15 10.5L25.05 4.215C25.1918 4.1168 25.3521 4.04839 25.5211 4.01392C25.6901 3.97945 25.8643 3.97963 26.0333 4.01445C26.2022 4.04927 26.3623 4.118 26.5039 4.21649C26.6455 4.31498 26.7657 4.44118 26.8571 4.58745C26.9485 4.73372 27.0093 4.89702 27.0358 5.06746C27.0622 5.2379 27.0539 5.41195 27.0111 5.57906C26.9684 5.74617 26.8922 5.90287 26.7872 6.03968C26.6821 6.17649 26.5504 6.29057 26.4 6.375Z" fill="#2BC2E1" />
                          </ShareEmailIcon>
                        </ShareOptionIconEmail>
                        <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
                      </ShareOption>
                    </ShareModalBody>
                    <ShareModalFooter>
                      <ShareLinkBox>
                        <ShareLinkLabel>{t('common.shareModal.linkLabel')}</ShareLinkLabel>
                        <ShareLinkValue>{projectUrl}</ShareLinkValue>
                      </ShareLinkBox>
                      <ShareCopyButton type="button" onClick={handleCopyShareLink}>
                        <ShareCopyIcon
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden
                          focusable="false"
                        >
                          <path d="M16.6665 6.66699H8.33317C7.4127 6.66699 6.6665 7.41318 6.6665 8.33366V16.667C6.6665 17.5875 7.4127 18.3337 8.33317 18.3337H16.6665C17.587 18.3337 18.3332 17.5875 18.3332 16.667V8.33366C18.3332 7.41318 17.587 6.66699 16.6665 6.66699Z" stroke="#676767" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M3.33317 13.3337C2.4165 13.3337 1.6665 12.5837 1.6665 11.667V3.33366C1.6665 2.41699 2.4165 1.66699 3.33317 1.66699H11.6665C12.5832 1.66699 13.3332 2.41699 13.3332 3.33366" stroke="#676767" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </ShareCopyIcon>
                        {t('common.shareModal.copyLink')}
                      </ShareCopyButton>
                    </ShareModalFooter>
                  </ShareModalCard>
                </ShareModalOverlay>
              ) : null}
            </CompleteOverlay>
          ) : null}
        </Content>
      </Main>
    </PageBackground>
  );
};

export default NewProjectWeb;

const PageBackground = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.grayScale[6]};
`;

const Main = styled.main`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const Content = styled.div`
  width: 1200px;;
  max-width: 1500px;
  margin: 0 auto;
  padding: 0;
`;

const Wrap = styled.div<{ $isStep3?: boolean }>`
  display: grid;
  grid-template-columns: 578px 600px;
  align-items: ${({ $isStep3 }) => ($isStep3 ? 'stretch' : 'start')};
  gap: 60px;
  padding: ${convertToVwWeb(12)} 0;
`;

const Left = styled.section<{ $isStep3?: boolean }>`
  padding-top: 0;
  min-height: ${({ $isStep3 }) => ($isStep3 ? '0' : convertToVwWeb(750))};
  display: flex;
  flex-direction: column;
`;

const BrandPill = styled.button`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  border: 2px solid #dfdfdf;
  background: #fff;
  padding: 12px 20px;
  cursor: pointer;
  box-shadow: none;
  outline: none;

  &:hover {
    border-radius: 100px;
    border: 2px solid #929292;
    background: #ffffff;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    border-radius: 100px;
    border: 2px solid #929292;
    background: #ebfcff;
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.15);
  }

  &:active {
    border-radius: 100px;
    border: 2px solid #929292;
    background: #EBFCFF;
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.15);
  }
`;

const BrandLogo = styled.img`
  height: 30px;
`;

const HomeIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const Stepper = styled.div<{ $isStep3?: boolean }>`
  margin-top: ${({ $isStep3 }) => ($isStep3 ? '84px' : '120px')};
  display: flex;
  align-items: center;
  width: fit-content;
`;

const StepDot = styled.div<{ $active: boolean }>`
  display: flex;
  width: ${({ $active }) => ($active ? '30px' : '20px')};
  height: ${({ $active }) => ($active ? '30px' : '20px')};
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  box-sizing: border-box;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  color: #ffffff;
  background: ${({ $active }) => ($active ? '#2BC2E1' : '#DFDFDF')};
`;

const StepLine = styled.div`
  flex: 0 0 44px;
  width: 44px;
  height: 2px;
  background: #dfdfdf;
`;

const LeftTitle = styled.div`
  margin-top: ${convertToVwWeb(40)};
  white-space: pre-line;
  color: #17171b;
  font-family: Pretendard;
  font-size: 44px;
  font-weight: 400;
  line-height: 130%;
`;

const LeftDesc = styled.div`
  margin-top: ${convertToVwWeb(26)};
  white-space: pre-line;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
`;

const Right = styled.section`
  display: flex;
  justify-content: flex-start;
`;

const Card = styled.div`
  width: ${convertToVwWeb(630)};
  height: 854px;
  max-height: calc(100vh - 64px);
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardBody = styled.div`
  padding: 28px 28px 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: 24px;
`;

const CardFooter = styled.div`
  padding: 16px 31px 24px;
  border-top: 1px solid #eeeeee;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FooterBtn = styled.button<{ $variant: 'primary' | 'secondary' }>`
  display: flex;
  width: 130px;
  height: 54px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: ${({ $variant }) => ($variant === 'secondary' ? '1px solid #DFDFDF' : 'none')};
  background: ${({ $variant, disabled }) => (disabled ? '#DFDFDF' : $variant === 'primary' ? '#026478' : '#FFFFFF')};
  color: ${({ $variant, disabled }) => (disabled ? '#BCBCBC' : $variant === 'primary' ? '#ffffff' : '#676767')};
  ${theme.typography.button.regular20}
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const MediaTitle = styled.div`
  color: var(--grey-scale-90-main-text, #17171B);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin-bottom: 12px;
`;

const FieldTitle = styled.div`
  color: var(--grey-scale-90-main-text, #17171B);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin-bottom: 10px;
`;

const ChoiceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

const ChoiceCard = styled.button<{ $active: boolean }>`
  text-align: left;
  padding: 10px 18px;
  border-radius: 10px;
  border: 2px solid ${({ $active }) => ($active ? '#2BC2E1' : '#eeeeee')};
  background: ${({ $active }) => ($active ? '#EAFBFF' : '#ffffff')};
  cursor: pointer;
`;

const ChoiceTop = styled.div<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 150%;
`;

const ChoiceSub = styled.div<{ $active: boolean }>`
  margin-top: 6px;
  color: ${({ $active }) => ($active ? '#676767' : '#BCBCBC')};
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
`;

const RadioBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  opacity: ${({ $active }) => ($active ? 1 : 0.8)};
`;

const RadioDot = styled.span<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const InputBox = styled.div<{ $disabled?: boolean; $error?: boolean; $active?: boolean }>`
  background-color: ${props => (props.$disabled ? theme.colors.grayScale[10] : 'white')};
  border: ${props => (props.$error ? '1.6px solid #ff0000' : props.$active ? `1.6px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`)};
  border-radius: 4px;
  padding: 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'text')};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-height: 64px;
  box-sizing: border-box;
  justify-content: center;

  &:hover {
    border-color: ${props => (props.$error ? '#ff0000' : theme.colors.primaryBlue[40])};
  }
`;

const InputLabel = styled.label`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  font-size: 14px;
  cursor: text;
`;

const StyledInputField = styled.input<{ $disabled?: boolean; $error?: boolean }>`
  color: ${props => (props.$disabled ? theme.colors.grayScale[50] : props.$error ? '#ff0000' : theme.colors.grayScale[90])};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const GoalUnderlineInput = styled(StyledInputField)`
  margin-top: 10px;
  padding: 0 0 4px;
  border-bottom: 1px solid ${({ $error }) => ($error ? '#ff0000' : theme.colors.grayScale[20])};
  color: ${({ $error }) => ($error ? '#ff0000' : '#17171B')};
  font-family: Pretendard;
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;

  &::placeholder {
    color: ${theme.colors.grayScale[60]};
  }

  &:hover,
  &:focus {
    border-bottom-color: ${({ $error }) => ($error ? '#ff0000' : theme.colors.primaryBlue[40])};
  }
`;

const GoalPreview = styled.div`
  margin-top: 8px;
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
`;

const StyledTextAreaField = styled.textarea<{ $disabled?: boolean; $error?: boolean }>`
  color: ${props => (props.$disabled ? theme.colors.grayScale[50] : props.$error ? '#ff0000' : theme.colors.grayScale[90])};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  resize: none;
  overflow-y: auto;
  min-height: 30px;
  word-break: keep-all;
  overflow-wrap: break-word;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const HintRow = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HintText = styled.div`
  margin-top: 4px;
  color: #357b89;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`;

const UrlPreview = styled.div`
  margin-top: 10px;
  border-radius: 8px;
  background: #f0f1f2;
  padding: 12px;
`;

const UrlLabel = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`;

const UrlValue = styled.div`
  margin-top: 4px;
  color: #17171b;
  font-family: "Roboto Mono";
  font-size: 16px;
  font-style: italic;
  font-weight: 400;
  line-height: 20px;
`;

const UrlValueHighlight = styled.span`
  color: #026478;
  font-family: "Roboto Mono";
  font-size: 16px;
  font-style: italic;
  font-weight: 700;
  line-height: 20px;
`;

const MediaTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const MediaTypeRadioBtn = styled.button<{ $active: boolean }>`
  min-height: 44px;
  padding: 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #3b3b3b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
`;

const MediaTypeRadioDot = styled.span<{ $active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1.6px solid #e5e5e5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
    $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const UploadRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
`;

const UploadHint = styled.div<{ $disabled?: boolean }>`
  height: 56px;
  border-radius: 4px;
  border: 1px solid #dfdfdf;
  background: #f0f1f2;
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  display: flex;
  align-items: center;
  padding: 0 12px;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;

const UploadBtn = styled.button`
  height: 56px;
  width: 130px;
  border-radius: 4px;
  border: 1px solid var(--GreyScale-40, #DFDFDF);
  background: var(--GreyScale-0, #FFFFFF);
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;

const MediaPreview = styled.div`
  margin-top: 16px;
  height: 240px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  background: #f0f1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Step2MediaPreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Step2MediaPreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const MediaPreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MediaPreviewEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
`;

const MediaPreviewEmptyText = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MediaPreviewIcon = styled.img`
  width: 74px;
  height: 74px;
  object-fit: contain;
  opacity: 0.9;
`;

const MediaPreviewVideoIcon = styled.svg`
  width: 70px;
  height: 70px;
`;

const MediaPreviewNoneIcon = styled.svg`
  width: 50px;
  height: 50px;
`;

const MediaPreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MediaPreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoUrlRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: 6px;
  border: 2px solid
    ${({ $state }) =>
    $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
    font-size: 20px;
  }
`;

const VideoUrlClear = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-top: 8px;
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: 56px;
  width: 130px;
  border-radius: 4px;
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

// NOTE: Step3 이미지 썸네일 UI는 RichTextEditor 내부로 이동

const Step3Scroll = styled.div`
  margin-top: 12px;
  height: 535px;
  overflow-y: auto;
  padding-right: 2px;
`;

const Step5Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Step5Scroll = styled.div`
  margin-top: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const Step3AddPhotoBtn = styled.button`
  margin-top: 16px;
  height: 56px;
  min-height: 56px;
  width: 100%;
  border-radius: 12px;
  border: 2px solid #2bc2e1;
  background: #eafbff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Step3AddPhotoIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const TipsWrap = styled.div<{ $isStep3?: boolean }>`
  margin-top: auto;
  padding-top: ${convertToVwWeb(40)};
  max-width: 520px;
  display: ${({ $isStep3 }) => ($isStep3 ? 'flex' : 'block')};
  flex-direction: column;
  box-sizing: border-box;
`;

const TipsTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin-top: ${convertToVwWeb(20)};
`;

const TipsDesc = styled.div`
  margin-top: 8px;
  white-space: pre-line;
  color: #676767;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const TipsGrid = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TipCard = styled.div<{ $span2?: boolean }>`
  border-radius: 8px;
  border: 1px solid #EEE;
  max-width : 271px;
  background: #fff;
  padding: 12px;
  color: #111827;
  ${({ $span2 }) => ($span2 ? 'grid-column: 1 / span 2;' : '')}
`;

const TipHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TipNum = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #357b89;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  flex: 0 0 auto;
`;

const TipName = styled.div`
  color: #111827;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const TipText = styled.div`
  margin-top: 8px;
  color: #676767;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const MembershipList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MembershipScroll = styled.div`
  margin-top: 12px;
  overflow-y: auto;
  padding-right: 2px;
`;

const MembershipCard = styled.button<{ $active: boolean }>`
  text-align: left;
  padding: 24px;
  border-radius: 10px;
  border: ${({ $active }) => ($active ? '2.4px solid #2BC2E1' : '1px solid #EEE')};
  background: ${({ $active }) => ($active ? '#EBFCFF' : '#FFFFFF')};
  color: ${({ $active }) => ($active ? '#17171b' : '#676767')};
  cursor: pointer;

  &:hover {
    background-color: #F8FDFF;
  }
`;

const MembershipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MembershipName = styled.div<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  font-family: Pretendard;
  font-size: ${({ $active }) => ($active ? '32px' : '24px')};
  font-style: normal;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  line-height: ${({ $active }) => ($active ? '28px' : '120%')};
`;

const MembershipDesc = styled.div`
  margin-top: 18px;
  padding-left: 28px;
  color: inherit;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const MembershipChecks = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 28px;
`;

const MembershipCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CheckIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex: 0 0 auto;
`;

const ReviewTopTitle = styled.div`
  color: var(--grey-scale-90-main-text, #17171B);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin-bottom: 10px;
`;

const ReviewSectionBox = styled.section`
  margin-top: 16px;
`;

const ReviewSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ReviewSectionTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const ReviewEditBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border-radius: 100px;
  border: 1px solid #82e8ff;
  background: #ffffff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
`;

const ReviewRows = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
  min-width: 0;
`;

const ReviewRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  column-gap: 18px;
  align-items: start;
`;

const ReviewRowVertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** 2열 그리드에서 한 행 전체 너비 (프로젝트 제목 등) */
const ReviewRowVerticalFull = styled(ReviewRowVertical)`
  grid-column: 1 / -1;
  min-width: 0;
`;

const ReviewRowLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const ReviewRowValue = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  min-width: 0;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

/** 프로젝트 제목 — 설정 화면 입력(562px)과 동일 너비로 줄바꿈 기준 통일 */
const ReviewRowTitleValue = styled(ReviewRowValue)`
  max-width: 450px;
`;

/** 프로젝트 URL — 한 줄로 길게 이어지지 않도록 */
const ReviewRowUrlValue = styled(ReviewRowValue)`
  word-break: break-all;
`;

const ReviewDivider = styled.div`
  margin: 18px 0 0;
  height: 1px;
  background: #eeeeee;
`;

const ReviewMediaPreview = styled.div`
  margin-top: 14px;
  position: relative;
  height: 240px;
  border-radius: 10px;
  border: 2.4px solid #2bc2e1;
  overflow: hidden;
  background: #f0f1f2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ReviewMediaBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  background: #2bc2e1;
  color: #ffffff;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
`;

const ReviewStoryHtml = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: pre-wrap;

  button[data-embed='delete-btn'] {
    display: none !important;
  }

  figure[data-embed='pinned-image'] {
    margin: 0 0 12px;
    border-radius: 10px;
    overflow: hidden;
    border: 2.4px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='story-image'] {
    margin: 12px 0;
    border-radius: 10px;
    overflow: hidden;
    border: 2.4px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='pinned-image'] img[data-embed='image'],
  figure[data-embed='story-image'] > img[data-embed='image'] {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }
`;

const ReviewStoryBox = styled.div`
  margin-top: 10px;
  border-radius: 10px;
  border: none;
  background: transparent;
  padding: 0;
`;

const ReviewStoryContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? 'none' : '360px')};
`;

const ReviewStoryEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ReviewStoryToggleWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ReviewStoryToggle = styled.button`
  margin: 14px auto 0;
  height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid #bcbcbc;
  background: #fff;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
`;

const ReviewStoryToggleIcon = styled.svg<{ $expanded: boolean }>`
  width: 13px;
  height: 8px;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const ReviewMembershipName = styled.div`
  margin-top: 14px;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const ReviewMembershipDesc = styled.div`
  margin-top: 8px;
  color: #676767;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ReviewMediaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ReviewMediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ReviewMediaIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

// preview modal (large)
const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PreviewShareHand = styled.img`
  position: absolute;
  bottom: 0;
  right: 40px;
  width: 100px;
  height: auto;
  z-index: 1;
`;

const PreviewHeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 600;
`;



// create complete overlay
const CompleteOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${convertToVwWeb(24)};

  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(80px);

  &::before {
    content: '';
    position: absolute;
    inset: -20%;
    background: linear-gradient(
      90deg,
      rgba(43, 194, 225, 0.55) 0%,
      rgba(255, 255, 255, 0.55) 45%,
      rgba(255, 223, 166, 0.55) 100%
    );
    background-size: 200% 200%;
    animation: completeBgMove 4s ease-in-out infinite alternate;
    filter: blur(80px);
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.02);
  }

  @keyframes completeBgMove {
    0% {
      background-position: 0% 50%;
      transform: translate3d(-2%, 0, 0);
    }
    100% {
      background-position: 100% 50%;
      transform: translate3d(2%, 0, 0);
    }
  }
`;

const CompleteDialog = styled.div`
  position: relative;
  z-index: 1;
  width: min(100%, ${convertToVwWeb(760)});
  border-radius: ${convertToVwWeb(20)};
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 ${convertToVwWeb(8)} ${convertToVwWeb(24)} rgba(0, 0, 0, 0.08);
  padding: ${convertToVwWeb(44)} ${convertToVwWeb(52)} ${convertToVwWeb(36)};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CompleteLogo = styled.img`
  height: ${convertToVwWeb(44)};
  object-fit: contain;
  margin-bottom: 20px;
`;

const CompleteLottieWrap = styled.div`
  width: ${convertToVwWeb(140)};
  height: ${convertToVwWeb(140)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${convertToVwWeb(4)} 0 ${convertToVwWeb(14)};
  margin-bottom: 20px;
`;

const CompleteTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(44)};
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  margin-top: ${convertToVwWeb(6)};
  margin-bottom: 40px;
`;

const CompleteButtons = styled.div`
  width: 100%;
  margin-top: ${convertToVwWeb(36)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(16)};
  align-items: center;
`;

const CompleteButton = styled.button`
  width: min(100%, ${convertToVwWeb(520)});
  height: ${convertToVwWeb(64)};
  border-radius: ${convertToVwWeb(50)};
  border: 1.4px solid #2bc2e1;
  background: #ffffff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  transition: background 0.2s;

  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background: #EBFCFF;
  }

  &:active, &:focus {
    background: #B9F3FF;
  }
`;

const SkipButton = styled.button`
  margin-top: ${convertToVwWeb(22)};
  border: none;
  background: transparent;
  color: #676767;
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-decoration-line: underline;
  cursor: pointer;
`;

const ShareModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${convertToVwWeb(24)};
`;

const ShareModalCard = styled.div`
  width: 644px;
  max-width: calc(100vw - 48px);
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ShareModalHeader = styled.div`
  padding: 32px 30px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const ShareModalTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ShareModalTitle = styled.h2`
  margin: 0;
  color: #17171b;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const ShareModalDesc = styled.p`
  margin: 0;
  color: #676767;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const ShareModalCloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ShareModalCloseIcon = styled.svg`
  width: 100%;
  height: 100%;
`;

const ShareModalBody = styled.div`
  padding: 0 30px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShareOption = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
`;

const ShareOptionIconKakao = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 999px;
  background: #f9e000;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }
`;

const ShareOptionIconEmail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 999px;
  background: #ebfcff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ShareEmailIcon = styled.svg`
  width: 30px;
  height: 24px;
  flex: 0 0 auto;
`;

const ShareOptionText = styled.span`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const ShareModalFooter = styled.div`
  background: #f8f8f8;
  border-radius: 0 0 12px 12px;
  padding: 20px 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ShareLinkBox = styled.div`
  flex: 1;
  min-width: 0;
  height: 70px;
  border: 1px solid #929292;
  border-right: none;
  border-radius: 10px 0 0 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 16px;
`;

const ShareLinkLabel = styled.span`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ShareLinkValue = styled.span`
  color: #676767;
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  display: block;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  text-overflow: clip;
  padding-bottom: 2px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ShareCopyButton = styled.button`
  flex: 0 0 auto;
  height: 70px;
  border-radius: 0 10px 10px 0;
  border-top: 1px solid #929292;
  border-right: 1px solid #929292;
  border-bottom: 1px solid #929292;
  border-left: 1px solid #929292;
  background: #fff;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-align: center;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 32px;
  cursor: pointer;
`;

const ShareCopyIcon = styled.svg`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
`;

